// filepath: apps/frontend/src/services/api.ts
import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  timeout: 30000,
  // Send the httpOnly auth cookies with every request
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const UNSAFE_METHODS = ['post', 'put', 'patch', 'delete'];

const readCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

let isRefreshing = false;
let refreshSubscribers: Array<() => void> = [];

const onRefreshed = () => {
  refreshSubscribers.forEach((callback) => callback());
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback: () => void) => {
  refreshSubscribers.push(callback);
};

const redirectToLogin = () => {
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// Request interceptor — attach the double-submit CSRF token for state-changing calls
api.interceptors.request.use(
  (config) => {
    const method = (config.method || 'get').toLowerCase();
    if (UNSAFE_METHODS.includes(method)) {
      const csrfToken = readCookie('csrf_token');
      if (csrfToken) {
        config.headers = config.headers || {};
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }
    // File uploads: drop the instance default 'application/json' so the browser sets
    // 'multipart/form-data' WITH a boundary — otherwise multer cannot parse the upload
    // and the backend sees no file (400 "A PDF file is required").
    if (config.data instanceof FormData && config.headers) {
      const headers = config.headers as any;
      if (typeof headers.delete === 'function') {
        headers.delete('Content-Type');
      } else {
        delete headers['Content-Type'];
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor — transparently refresh the session on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url: string = originalRequest?.url || '';
    const isAuthEndpoint = url.includes('/api/v1/auth/refresh') || url.includes('/api/v1/auth/login');

    // Plan quota exceeded — surface an upgrade prompt globally.
    if (error.response?.status === 402) {
      const raw = error.response?.data?.message;
      const message = typeof raw === 'object' ? raw?.message : raw;
      window.dispatchEvent(
        new CustomEvent('rssign:quota-exceeded', { detail: message || 'Upgrade required' }),
      );
    }

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.skipAuthRefresh &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve) => {
          addRefreshSubscriber(() => resolve(api(originalRequest)));
        });
      }

      isRefreshing = true;

      try {
        // Cookie-based refresh: the server reads the refresh_token cookie and
        // sets fresh cookies on the response. No tokens are handled in JS.
        await api.post('/api/v1/auth/refresh', {});
        onRefreshed();
        return api(originalRequest);
      } catch (refreshError) {
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default api;
